import React from 'react';
import PhoneInput, { isPossiblePhoneNumber, isValidPhoneNumber } from 'react-phone-number-input';
import type { Country } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Phone } from 'lucide-react';
import { useOptionalI18n } from '@/i18n';
import { localizeText } from '@/i18n/options';

interface PhoneNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  onValidationChange?: (isValid: boolean) => void;
  defaultCountry?: string;
}

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  value,
  onChange,
  placeholder = 'Enter phone number',
  required = false,
  disabled = false,
  error,
  onValidationChange,
  defaultCountry = 'MY', // Malaysia as default
}) => {
  const { locale } = useOptionalI18n();
  const [touched, setTouched] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string | null>(null);

  // Validate phone number
  const validatePhone = (phoneValue: string) => {
    if (!phoneValue) {
      if (required) {
        setValidationError(localizeText(locale, 'Phone number is required'));
        onValidationChange?.(false);
        return false;
      }
      setValidationError(null);
      onValidationChange?.(true);
      return true;
    }

    // Check if it's a possible phone number format
    if (!isPossiblePhoneNumber(phoneValue)) {
      setValidationError(localizeText(locale, 'Invalid phone number format'));
      onValidationChange?.(false);
      return false;
    }

    // Check if it's a valid phone number
    if (!isValidPhoneNumber(phoneValue)) {
      setValidationError(localizeText(locale, 'Please enter a valid phone number'));
      onValidationChange?.(false);
      return false;
    }

    setValidationError(null);
    onValidationChange?.(true);
    return true;
  };

  const handleChange = (newValue: string | undefined) => {
    const phoneValue = newValue || '';
    onChange(phoneValue);

    // Only validate if field has been touched
    if (touched) {
      validatePhone(phoneValue);
    }
  };

  const handleBlur = () => {
    setTouched(true);
    validatePhone(value);
  };

  const displayError = error || (touched ? validationError : null);

  return (
    <div className="w-full">
      <div className="relative">
        <PhoneInput
          international
          defaultCountry={defaultCountry as Country}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={localizeText(locale, placeholder)}
          disabled={disabled}
          countryCallingCodeEditable={false}
          className={`phone-input-wrapper ${displayError ? 'error' : ''}`}
          numberInputProps={{
            className: `input-field pl-10 w-full ${
              displayError
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-primary focus:border-primary'
            }`,
          }}
        />
        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none z-10" />
      </div>
      {displayError && (
        <p className="text-red-500 text-sm mt-1">{displayError}</p>
      )}
    </div>
  );
};

export default PhoneNumberInput;
